// graphClipboardActions — the host-side half of the node clipboard: the key
// chords, the OS clipboard I/O, and where a paste lands.
//
// nodeClipboard.js owns the RULES (what may be copied, what a payload means,
// how ids are remapped). This module owns the GESTURE, and is split out so both
// graph editors — BlueprintEditor (body/face) and EventGraph (main, group and
// function canvases) — share one implementation instead of four copies of the
// same keydown handler.
//
// Transport is the OS clipboard as JSON text, deliberately: a payload then
// crosses browser tabs, crosses characters, survives a reload, and can be
// pasted into the agent panel or into Slack — which is what was asked for. The
// cost is that clipboard reads can be refused by the browser, so every failure
// here is reported with its remedy and NEVER silently swallowed.
//
// There is no in-app fallback buffer. Two sources of truth for one gesture is
// the stale-draft failure the repo's UX rules name; when the clipboard is
// unavailable the honest answer is to say so and point at File ▸ Import.

import {
  extractSelection,
  parseEnvelope,
  serializeEnvelope,
  canPaste,
  remapIds,
  resolveRequires,
  describeMissing,
} from './nodeClipboard.js'

// ── Key chords ──────────────────────────────────────────────────────────────
//
// Same shape and the same guard as graphHistory.isUndoKeyEvent: a chord fired
// inside an editable field must keep the browser's native behaviour, or copying
// text out of a node's name field would copy the node instead.

function isEditableTarget(t) {
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
}

function isChord(e, letter) {
  if (!e || !(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return false
  if (String(e.key ?? '').toLowerCase() !== letter) return false
  return !isEditableTarget(e.target)
}

export const isCopyKeyEvent = (e) => isChord(e, 'c')
export const isCutKeyEvent = (e) => isChord(e, 'x')
export const isPasteKeyEvent = (e) => isChord(e, 'v')

// ── Where a paste lands ─────────────────────────────────────────────────────

/**
 * Offset that puts a payload's top-left corner at `target` in flow coordinates,
 * preserving the relative layout of everything in it.
 *
 * With no target (a keyboard paste with no known pointer position) the payload
 * is nudged by a fixed step instead, matching the existing single-node
 * Duplicate — so a paste is always visibly a new thing rather than landing
 * exactly on top of the nodes it was copied from.
 */
export const PASTE_NUDGE = { x: 40, y: 40 }

export function pasteOffsetFor(nodes, target = null) {
  if (!target) return { ...PASTE_NUDGE }
  let minX = Infinity
  let minY = Infinity
  for (const n of nodes ?? []) {
    minX = Math.min(minX, n.position?.x ?? 0)
    minY = Math.min(minY, n.position?.y ?? 0)
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return { ...PASTE_NUDGE }
  return { x: target.x - minX, y: target.y - minY }
}

// ── OS clipboard I/O ────────────────────────────────────────────────────────

/**
 * `navigator.clipboard`, or null. Injected in tests; read lazily so this module
 * stays importable under `node --test`.
 */
function clipboardApi(injected) {
  if (injected !== undefined) return injected
  if (typeof navigator === 'undefined') return null
  return navigator.clipboard ?? null
}

export async function writeEnvelopeToClipboard(envelope, { clipboard } = {}) {
  const api = clipboardApi(clipboard)
  const text = serializeEnvelope(envelope)
  // Debug mirror only (window.__* convention). Never read by the paste path —
  // that would resurrect the two-sources-of-truth problem this module avoids.
  if (typeof window !== 'undefined') window.__NODE_CLIPBOARD__ = envelope
  if (!api?.writeText) {
    return { ok: false, reason: 'this browser exposes no clipboard write access' }
  }
  try {
    await api.writeText(text)
    return { ok: true, text }
  } catch (e) {
    return { ok: false, reason: `the browser refused the clipboard write (${e?.message ?? e})` }
  }
}

export async function readEnvelopeFromClipboard({ clipboard, text } = {}) {
  // An explicit `text` is the DOM paste-event path: an event's clipboardData
  // carries the payload with no permission prompt, so it is preferred wherever
  // a real paste event is available.
  if (typeof text === 'string') return parseEnvelope(text)

  const api = clipboardApi(clipboard)
  if (!api?.readText) {
    return {
      ok: false,
      reason: 'this browser exposes no clipboard read access — use File ▸ Import blueprint… instead',
    }
  }
  try {
    return parseEnvelope(await api.readText())
  } catch (e) {
    return {
      ok: false,
      reason: `the browser blocked the clipboard read (${e?.message ?? e}) — use File ▸ Import blueprint… instead`,
    }
  }
}

// ── The three gestures ──────────────────────────────────────────────────────
//
// Each takes a `host` describing this canvas. Every field is injected because
// the two editors differ in all of them: id scheme, history ownership, how the
// graph is read, and how a report is surfaced.
//
//   host = {
//     kind,                 // 'body' | 'face' | 'events'
//     getGraph(),           // () => { nodes, edges }   (live, incl. selection)
//     getDeclarations(),    // () => { variables, functions }
//     getAvailable(),       // () => { clips, audioAssetIds, variables, functions }
//     makeMinter(),         // () => (() => id)  a FRESH minter for one paste,
//                           //   seeded past everything in the LIVE graph
//     mintId(),             // fallback when makeMinter is absent
//     applyPaste(nodes, edges),   // add to the canvas (and push history)
//     removeNodes(ids),           // for cut (and push history)
//     report({ level, message }), // surface to the user — never console-only
//     source,               // provenance stamped into the envelope
//   }

/** Selected nodes, or [] — the one place "what is copied" is decided. */
export function selectedNodeIds(graph) {
  return (graph?.nodes ?? []).filter(n => n.selected).map(n => n.id)
}

export async function copySelection(host, { clipboard } = {}) {
  const graph = host.getGraph()
  const ids = selectedNodeIds(graph)
  if (ids.length === 0) {
    // Not an error: Ctrl+C with nothing selected is a no-op everywhere. Saying
    // so keeps it from reading as a broken feature.
    host.report?.({ level: 'info', message: 'Nothing selected to copy.' })
    return { ok: false, empty: true }
  }
  const decl = host.getDeclarations?.() ?? {}
  const envelope = extractSelection(graph, ids, {
    kind: host.kind,
    source: host.source ?? {},
    variables: decl.variables ?? [],
    functions: decl.functions ?? {},
  })
  const written = await writeEnvelopeToClipboard(envelope, { clipboard })
  if (!written.ok) {
    host.report?.({ level: 'error', message: `Copy failed: ${written.reason}` })
    return { ok: false, reason: written.reason }
  }
  const n = envelope.graph.nodes.length
  const e = envelope.graph.edges.length
  host.report?.({
    level: 'info',
    message: `Copied ${n} node${n === 1 ? '' : 's'}${e ? ` and ${e} connection${e === 1 ? '' : 's'}` : ''}.`,
  })
  return { ok: true, envelope }
}

export async function cutSelection(host, { clipboard } = {}) {
  const copied = await copySelection(host, { clipboard })
  // Only remove once the payload is safely on the clipboard — a cut that copied
  // nothing and deleted anyway is data loss.
  if (!copied.ok) return copied
  const ids = copied.envelope.graph.nodes.map(n => n.id)
  host.removeNodes?.(ids)
  return { ok: true, envelope: copied.envelope, removed: ids }
}

export async function pasteIntoGraph(host, { clipboard, text, at = null } = {}) {
  const parsed = await readEnvelopeFromClipboard({ clipboard, text })
  if (!parsed.ok) {
    // Foreign clipboard content is a MISS, not a failure: the user pasted
    // something that is not ours into a canvas. Say nothing rather than raising
    // a dialog on every stray Ctrl+V.
    if (parsed.notOurs) return { ok: false, notOurs: true, reason: parsed.reason }
    host.report?.({ level: 'error', message: `Paste failed: ${parsed.reason}` })
    return { ok: false, reason: parsed.reason }
  }
  const envelope = parsed.envelope

  const allowed = canPaste(envelope.kind, host.kind)
  if (!allowed.ok) {
    host.report?.({ level: 'error', message: `Cannot paste here — ${allowed.reason}.` })
    return { ok: false, reason: allowed.reason }
  }

  const offset = pasteOffsetFor(envelope.graph.nodes, at)
  // A minter is built PER PASTE and seeded from the live graph. The editors'
  // own `nextId` seeds its counter from the mount-time localStorage snapshot,
  // so once a `?blueprint=` URL or a cloud load replaces the graph with one
  // whose ids run higher it hands back ids that already exist — a single add
  // per render mostly survives that, a multi-node paste does not. Measured: a
  // two-node paste into the shipped conversational blueprint produced a
  // duplicate id on the first try.
  const mintId = host.makeMinter ? host.makeMinter() : host.mintId
  const { nodes, edges, idMap } = remapIds(envelope, { mintId, offset })

  host.applyPaste(nodes, edges)

  const resolution = resolveRequires(envelope, host.getAvailable?.() ?? {})
  const missing = describeMissing(resolution)
  const n = nodes.length
  host.report?.({
    level: missing ? 'warn' : 'info',
    message: `Pasted ${n} node${n === 1 ? '' : 's'}.${missing ? ` ${missing}.` : ''}`,
    resolution,
    envelope,
  })

  return { ok: true, nodes, edges, idMap, resolution }
}
